import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EngineService } from '@/services/engine';
import { VideoPlayer } from '@/components/VideoPlayer';
import { VideoPlayerAnalyzingAurora } from '@/components/VideoPlayer/VideoPlayerAnalyzingAurora';
import { StreamingMode } from '@/types/streaming';
import { useSourcesStore } from '@/stores/sources.store';
import { useEventFeedStore, useQueryPanelStore } from '@/stores';
import {
    SourceActionBar,
    SourceInfoPanel,
    SourcePageStatusBadge,
    SourceViewSkeleton,
} from './components';
import { isAnalyzingSourceStatus } from './sourceStatusDisplay';
import { EventEntitiesGrid } from '@/features/EventView/components';
import { AutoHideOnScroll, toast } from '@/components';
import { DateTimePicker } from '@/components/DateTime';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import type { Event } from '@/types/event';
import { FullSource, SourceType } from '@/types/source';
import { getSourceCacheKey } from '@/constants/cache-keys';
import { AppRoutes } from '@/constants/routes';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { useFeedFocus } from '@/hooks';
import { getEventStartTime } from '@/utils/event-duration';
import { toUtcTimestamp } from '@/utils/date';
import { buildBoundingBoxProps } from '@/utils/bounding-box';
import { deriveEventPlaybackRange } from '@/utils/event-playback-range';

interface SourceViewProps {
    sourceId: string;
    onTimeUpdate?: (currentTime: number) => void;
    onPlayingChange?: (isPlaying: boolean) => void;
    onLoopChange?: (loopEnabled: boolean) => void;
    onSeeked?: () => void;
    onSourceLoaded?: (source: FullSource) => void;
}

export function SourceView({
    sourceId,
    onTimeUpdate,
    onPlayingChange,
    onLoopChange,
    onSeeked,
    onSourceLoaded,
}: SourceViewProps) {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const location = useLocation();
    const [scrollContainer, setScrollContainer] =
        useState<HTMLDivElement | null>(null);
    const setFeedInteraction = useEventFeedStore(
        (state) => state.setFeedInteraction,
    );
    const addSource = useSourcesStore((state) => state.addSource);
    const updateSource = useSourcesStore((state) => state.updateSource);
    const getSourceById = useSourcesStore((state) => state.getSourceById);
    const setSelectedSources = useSourcesStore(
        (state) => state.setSelectedSources,
    );
    const currentSource = useSourcesStore((state) =>
        state.sourcesMap.get(sourceId),
    );

    const [pickerDateTime, setPickerDateTime] = useState<Date | undefined>();

    const { focusedItem: focusedEvent, setFocusedItem: setFocusedEvent } =
        useFeedFocus<Event>({
            entityId: sourceId,
            getId: (event) => event.id,
            setInteraction: setFeedInteraction,
        });

    // The picker takes over from the focused event, otherwise eventStartTime
    // below keeps winning and the archive never reloads.
    const handlePickerChange = (value?: Date) => {
        setPickerDateTime(value);
        if (value) setFocusedEvent(null);
    };

    useEffect(() => {
        if (!focusedEvent) return;
        setPickerDateTime(undefined);
    }, [focusedEvent]);

    const sourceProcessing = currentSource
        ? isAnalyzingSourceStatus(currentSource.status)
        : false;

    const {
        data: source,
        error: sourceError,
        isLoading: sourceLoading,
    } = useSWR(
        sourceId && (!currentSource || sourceProcessing)
            ? getSourceCacheKey(sourceId)
            : null,
        () => EngineService.getSource(sourceId),
        {
            fallbackData: currentSource,
            keepPreviousData: true,
            refreshInterval: sourceProcessing ? 3000 : 0,
        },
    );

    useEffect(() => {
        if (source) {
            const storedSource = getSourceById(source.id);

            if (!storedSource) {
                addSource(source);
            } else if (storedSource !== source) {
                updateSource(source.id, source);
            }
            setSelectedSources(new Set([source.id]));
            useQueryPanelStore.setState({
                draftSources: {
                    selected: new Set([source.id]),
                    selectedFolders: new Map(),
                    excluded: new Set(),
                    all: false,
                },
            });
            onSourceLoaded?.(getSourceById(source.id) || source);
        }
    }, [
        source,
        addSource,
        getSourceById,
        onSourceLoaded,
        updateSource,
        setSelectedSources,
    ]);

    useEffect(() => {
        if (sourceError) {
            toast.error(t('page.failedToLoad', { type: 'source' }));
        }
    }, [sourceError, t]);

    if (sourceLoading) {
        return <SourceViewSkeleton />;
    }

    if (sourceError || !source) {
        return <Navigate to={AppRoutes.SOURCES} replace />;
    }

    const enrichedSource = currentSource || source;

    const eventStartTime =
        focusedEvent?.frames && focusedEvent.frames.length > 0
            ? getEventStartTime(focusedEvent.frames, focusedEvent.focuses ?? [])
            : undefined;

    const pickerTimestamp = pickerDateTime
        ? toUtcTimestamp(pickerDateTime)
        : undefined;

    const startTime = eventStartTime ?? pickerTimestamp;

    const boundingBoxProps = focusedEvent
        ? buildBoundingBoxProps({
              event: focusedEvent,
              focuses: focusedEvent.focuses,
          })
        : undefined;

    const eventRange = focusedEvent
        ? (deriveEventPlaybackRange({
              event: focusedEvent,
              sourceType: enrichedSource.type,
              focuses: focusedEvent.focuses,
          }) ?? undefined)
        : undefined;

    const seekingToTime = Boolean(focusedEvent) || pickerDateTime !== undefined;

    const isSourceAnalyzing =
        isAnalyzingSourceStatus(enrichedSource.status) &&
        enrichedSource.type === SourceType.FILE;

    return (
        <div className="h-full flex flex-col relative overflow-hidden pt-14">
            <AutoHideOnScroll container={scrollContainer}>
                <SourceActionBar source={enrichedSource} />
            </AutoHideOnScroll>
            <div
                ref={setScrollContainer}
                className="flex-1 overflow-y-auto scrollbar-hide px-4"
            >
                <div className="flex flex-col gap-6 pt-8 pb-24 max-w-content mx-auto">
                    <div className="relative overflow-visible isolate">
                        <div className="relative z-10">
                            <VideoPlayer
                                src={enrichedSource.src || ''}
                                source={enrichedSource}
                                initialMode={
                                    seekingToTime
                                        ? StreamingMode.VOD
                                        : StreamingMode.LIVE
                                }
                                startTime={startTime}
                                boundingBoxProps={boundingBoxProps}
                                eventRange={eventRange}
                                defaultLoop={Boolean(focusedEvent)}
                                fullScreenTitle={enrichedSource.displayName}
                                controls
                                crop
                                onTimeUpdate={onTimeUpdate}
                                onPlayingChange={onPlayingChange}
                                onLoopChange={onLoopChange}
                                onSeeked={onSeeked}
                            />
                        </div>
                        {isSourceAnalyzing && (
                            <div className="analyzing-aurora-shell absolute -left-[55px] -right-[55px] top-0 bottom-[-60px] z-0 pointer-events-none overflow-visible">
                                <VideoPlayerAnalyzingAurora
                                    amplitude={1.0}
                                    blend={0.5}
                                    speed={1.2}
                                    heightReductionPx={10}
                                />
                            </div>
                        )}
                    </div>
                    <div className="flex w-full flex-col items-start gap-3">
                        <SourcePageStatusBadge source={enrichedSource} />
                        <div className="flex w-full items-center justify-between gap-3">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <h3 className="text-h3 max-w-full w-fit truncate text-white-100">
                                        {enrichedSource.displayName}
                                    </h3>
                                </TooltipTrigger>
                                <TooltipContent side="top" sideOffset={8}>
                                    {enrichedSource.displayName}
                                </TooltipContent>
                            </Tooltip>
                            {enrichedSource.type === SourceType.CAMERA && (
                                <DateTimePicker
                                    label={t('datePicker.selectDateTime', {
                                        ns: I18nNamespace.COMMON,
                                    })}
                                    value={pickerDateTime}
                                    onChange={handlePickerChange}
                                    maxValue={new Date()}
                                    showCalendar
                                />
                            )}
                        </div>
                    </div>
                    <SourceInfoPanel source={enrichedSource} />
                    {focusedEvent && focusedEvent.type !== 'SCENE' && (
                        <EventEntitiesGrid
                            eventId={focusedEvent.id}
                            returnUrl={location.pathname + location.search}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
